import {createAsyncThunk} from '@reduxjs/toolkit'
import axios from 'axios'
import {
  zadCustomerRefundByIdUrl,
  zadCustomerRefundCancelUrl,
  zadCustomerRefundCustomerSummaryUrl,
  zadCustomerRefundsUrl,
  zadCustomerReturnsUrl,
  zadCustomersUrl,
} from '../../api'
import {parseZadApiError} from '../zad/errors'
import {sanitizeZadCustomerRefundWritePayload} from '../zad/customerRefundHelpers'
import {isWalkInZadCustomerReturn} from '../zad/customerReturnHelpers'
import {buildZadListQuery} from '../zad/query'
import {
  CancelZadCustomerRefundRequest,
  CreateZadCustomerRefundRequest,
  FetchZadCustomerRefundByIdRequest,
  FetchZadCustomerRefundPickerRequest,
  FetchZadCustomerRefundsRequest,
  FetchZadCustomerRefundSummaryRequest,
} from './types'

export const fetchZadCustomerRefunds = createAsyncThunk(
  'zadCustomerRefunds/fetch',
  async (payload: FetchZadCustomerRefundsRequest, {rejectWithValue}) => {
    try {
      const res = await axios.get(`${zadCustomerRefundsUrl}?${buildZadListQuery(payload)}`)
      const {refunds, total, totalPages} = res.data || {}
      return {
        success: true as const,
        refunds: Array.isArray(refunds) ? refunds : [],
        total: typeof total === 'number' ? total : 0,
        totalPages: typeof totalPages === 'number' ? totalPages : 0,
      }
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)

export const fetchZadCustomerRefundById = createAsyncThunk(
  'zadCustomerRefunds/fetchById',
  async (payload: FetchZadCustomerRefundByIdRequest, {rejectWithValue}) => {
    try {
      const res = await axios.get(zadCustomerRefundByIdUrl(payload.id))
      return {
        success: true as const,
        refund: res.data.refund,
      }
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)

export const createZadCustomerRefund = createAsyncThunk(
  'zadCustomerRefunds/create',
  async (payload: CreateZadCustomerRefundRequest, {rejectWithValue}) => {
    try {
      const body = sanitizeZadCustomerRefundWritePayload(payload)
      const res = await axios.post(zadCustomerRefundsUrl, body)
      return {
        success: true as const,
        refund: res.data.refund,
      }
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)

export const cancelZadCustomerRefund = createAsyncThunk(
  'zadCustomerRefunds/cancel',
  async (payload: CancelZadCustomerRefundRequest, {rejectWithValue}) => {
    try {
      const res = await axios.post(zadCustomerRefundCancelUrl(payload.id), {
        reason: String(payload.reason || ''),
      })
      return {
        success: true as const,
        refund: res.data.refund,
      }
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)

export const fetchZadCustomerRefundSummary = createAsyncThunk(
  'zadCustomerRefunds/fetchCustomerSummary',
  async (payload: FetchZadCustomerRefundSummaryRequest, {rejectWithValue}) => {
    try {
      const res = await axios.get(zadCustomerRefundCustomerSummaryUrl(payload.customerId))
      const data = res.data || {}
      return {
        success: true as const,
        summary: {
          customer: data.customer,
          validatedSalesTotal: data.validatedSalesTotal,
          validatedReturnsTotal: data.validatedReturnsTotal,
          activePaymentsTotal: data.activePaymentsTotal,
          activeRefundsTotal: data.activeRefundsTotal,
          outstanding: data.outstanding,
          refundableCredit: data.refundableCredit,
          saleCount: data.saleCount || 0,
          paymentCount: data.paymentCount || 0,
          returnCount: data.returnCount,
          refundCount: data.refundCount,
        },
      }
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)

export const fetchZadCustomerRefundPickerCustomers = createAsyncThunk(
  'zadCustomerRefunds/fetchPickerCustomers',
  async (payload: FetchZadCustomerRefundPickerRequest | void, {rejectWithValue}) => {
    try {
      const res = await axios.get(
        `${zadCustomersUrl}?${buildZadListQuery({
          pageNumber: 1,
          pageCount: 100,
          sortField: 'createdAt',
          sortOrder: 'desc',
          isActive: 'true',
          search: payload?.search || '',
        })}`
      )
      const {customers} = res.data || {}
      return {
        success: true as const,
        customers: Array.isArray(customers) ? customers : [],
      }
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)

export const fetchZadCustomerRefundPickerReturns = createAsyncThunk(
  'zadCustomerRefunds/fetchPickerReturns',
  async (payload: FetchZadCustomerRefundPickerRequest, {rejectWithValue}) => {
    try {
      const res = await axios.get(
        `${zadCustomerReturnsUrl}?${buildZadListQuery({
          pageNumber: 1,
          pageCount: 100,
          sortField: 'returnDate',
          sortOrder: 'desc',
          customer: payload.customer || '',
          status: 'VALIDATED',
          search: payload.search || '',
        })}`
      )
      const {returns} = res.data || {}
      const list = Array.isArray(returns) ? returns : []
      return {
        success: true as const,
        returns: list.filter((row) => !isWalkInZadCustomerReturn(row)),
      }
    } catch (error) {
      return rejectWithValue(parseZadApiError(error))
    }
  }
)
